"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { gsap, useGSAP, NOT_REDUCED } from "./kit/motion";

/**
 * The routing graph canvas. Connector paths and the traced packet are computed
 * from the *measured* port positions (not hardcoded coordinates) so they always
 * meet the ports dead-centre — robust against font metrics and resizes. The
 * active path, highlighted nodes and the packet motion are driven by the parent
 * (`WorkflowDemo`), which cycles through visitor scenarios.
 */

interface Node {
  id: string;
  left: number;
  top: number;
  /** Box in the vertical (phone) layout: layered top-to-bottom, siblings
   *  side by side as half-width cards, the trunk runs through their gap. */
  v: { left: number; top: number; w: number };
  bar: string;
  iconBg: string;
  iconColor: string;
  icon: ReactNode;
  title: string;
  sub: string;
  tag?: string;
  ports: ("in" | "out")[];
}

const NODES: Node[] = [
  {
    id: "trigger",
    left: 16,
    top: 185,
    v: { left: 72, top: 16, w: 220 },
    bar: "bg-brand-600",
    iconBg: "bg-brand-600/[0.08]",
    iconColor: "text-brand-600",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
        <path d="M5 3l14 7-6 2-2 6z" />
      </svg>
    ),
    title: "Incoming click",
    sub: "trigger · on_visit",
    ports: ["out"],
  },
  {
    id: "switch",
    left: 290,
    top: 168,
    v: { left: 72, top: 128, w: 220 },
    bar: "bg-data-violet",
    iconBg: "bg-data-violet/[0.12]",
    iconColor: "text-data-violet",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d="M6 3v6a3 3 0 0 0 3 3h9M18 8l3 4-3 4M6 21v-6" />
      </svg>
    ),
    title: "Device router",
    sub: "switch · device.os",
    tag: "3 branches",
    ports: ["in", "out"],
  },
  {
    id: "ios",
    left: 620,
    top: 36,
    v: { left: 12, top: 270, w: 158 },
    bar: "bg-data-blue",
    iconBg: "bg-[#f3f3f5]",
    iconColor: "text-[#111]",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
        <path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.89-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.88 2.65 3.22 2.6 1.29-.05 1.78-.83 3.34-.83 1.56 0 2 .83 3.37.81 1.39-.03 2.27-1.27 3.12-2.53.98-1.45 1.39-2.85 1.41-2.92-.03-.01-2.71-1.04-2.74-4.13M14.6 4.42c.71-.86 1.19-2.06 1.06-3.25-1.02.04-2.26.68-2.99 1.54-.66.76-1.23 1.97-1.08 3.13 1.14.09 2.3-.58 3.01-1.42" />
      </svg>
    ),
    title: "App Store",
    sub: "if os = iOS · 41%",
    ports: ["in"],
  },
  {
    id: "android",
    left: 620,
    top: 150,
    v: { left: 194, top: 270, w: 158 },
    bar: "bg-data-green",
    iconBg: "bg-data-green/[0.14]",
    iconColor: "text-data-green",
    icon: (
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
        <path d="M4 2.5v19l16-9.5z" />
      </svg>
    ),
    title: "Google Play",
    sub: "if os = Android · 34%",
    ports: ["in"],
  },
  {
    id: "geo",
    left: 620,
    top: 300,
    v: { left: 72, top: 400, w: 220 },
    bar: "bg-data-violet",
    iconBg: "bg-data-blue/[0.12]",
    iconColor: "text-data-blue",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18" />
      </svg>
    ),
    title: "Region router",
    sub: "switch · geo.country",
    tag: "desktop & other",
    ports: ["in", "out"],
  },
  {
    id: "de",
    left: 905,
    top: 244,
    v: { left: 12, top: 542, w: 158 },
    bar: "bg-data-amber",
    iconBg: "bg-white",
    iconColor: "text-ink",
    icon: (
      <svg viewBox="0 0 24 16" width="20" height="13">
        <rect width="24" height="5.33" y="0" fill="#000" />
        <rect width="24" height="5.33" y="5.33" fill="#D00" />
        <rect width="24" height="5.34" y="10.66" fill="#FFCE00" />
      </svg>
    ),
    title: "German page",
    sub: "if geo = DE · 14%",
    ports: ["in"],
  },
  {
    id: "web",
    left: 905,
    top: 356,
    v: { left: 194, top: 542, w: 158 },
    bar: "bg-brand-800",
    iconBg: "bg-brand-600/[0.08]",
    iconColor: "text-brand-800",
    icon: (
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2}>
        <rect x="3" y="4" width="18" height="13" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
    title: "Web fallback",
    sub: "else default · 11%",
    ports: ["in"],
  },
];

/** Edge id convention: `${from}>${to}`. */
const EDGES = [
  { id: "trigger>switch", from: "trigger", to: "switch" },
  { id: "switch>ios", from: "switch", to: "ios" },
  { id: "switch>android", from: "switch", to: "android" },
  { id: "switch>geo", from: "switch", to: "geo" },
  { id: "geo>de", from: "geo", to: "de" },
  { id: "geo>web", from: "geo", to: "web" },
];

type Point = { x: number; y: number };

/** Logical stage size — node positions and the SVG viewBox are authored in
 *  this coordinate space; on narrow desktops the stage is CSS-scaled down to
 *  fit (#183) without touching these numbers. */
const STAGE_W = 1140;
const STAGE_H = 440;
/** Vertical (below md) stage: layered top-to-bottom graph. 12px gutters
 *  around a 340px band: full cards 220 wide centred, half cards 158 + 24 gap
 *  (the gap centre is the stage centre, where the trunk runs). */
const V_STAGE_W = 364;
const V_STAGE_H = 650;
/** Half-width cards split `sub` onto two lines so it fits. */
const V_HALF_W = 200;

interface WorkflowCanvasProps {
  /** Nodes on the active route (lit progressively as the packet arrives). */
  activeNodes: readonly string[];
  /** Ordered edge ids the packet travels for the current scenario. */
  packetEdges: readonly string[];
  /** Changes whenever a new scenario starts — restarts the packet. */
  scenarioKey: string;
  /** Run the packet motion (false for reduced-motion / off-screen). */
  animate: boolean;
}

function WorkflowNode({
  node,
  active,
  vertical,
  registerPort,
}: {
  node: Node;
  active: boolean;
  vertical: boolean;
  registerPort: (key: string, el: HTMLSpanElement | null) => void;
}) {
  // No translate-centring: ports are measured via layout offsets, which skip
  // transforms — so centre them with plain offsets (11px dot → 5.5px).
  const portClass = `absolute h-[11px] w-[11px] rounded-full border-2 bg-surface transition-colors duration-300 ${
    active ? "border-brand-600" : "border-line-3"
  }`;
  // Horizontal: in left / out right, mid-height. Vertical: in top-centre,
  // out bottom-centre.
  const inPos = vertical ? "-top-1.5 left-[calc(50%-5.5px)]" : "-left-1.5 top-[calc(50%-5.5px)]";
  const outPos = vertical ? "-bottom-1.5 left-[calc(50%-5.5px)]" : "-right-1.5 top-[calc(50%-5.5px)]";
  const pos = vertical ? node.v : { ...node, w: 188 };
  const split = vertical && pos.w < V_HALF_W;
  return (
    <div
      data-draw-node={node.id}
      className={`absolute z-[2] rounded-[13px] border bg-surface py-3 pl-4 pr-[13px] shadow-ref transition-[border-color,box-shadow] duration-300 ${
        active ? "border-brand-600 shadow-[0_0_0_3px_rgba(215,14,98,0.14)]" : "border-line-2"
      }`}
      style={{ left: pos.left, top: pos.top, width: pos.w }}
    >
      <span className={`absolute bottom-2.5 left-0 top-2.5 w-1 rounded ${node.bar}`} />
      {node.ports.includes("in") && (
        <span ref={(el) => registerPort(`${node.id}:in`, el)} className={`${inPos} ${portClass}`} />
      )}
      {node.ports.includes("out") && (
        <span ref={(el) => registerPort(`${node.id}:out`, el)} className={`${outPos} ${portClass}`} />
      )}
      <div className="flex items-center gap-[9px]">
        <span className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[7px] ${node.iconBg} ${node.iconColor}`}>
          {node.icon}
        </span>
        <span className="text-[13.5px] font-semibold tracking-[-0.01em]">{node.title}</span>
      </div>
      <div className="mt-[5px] font-jetbrains text-[11.5px] text-muted">
        {split
          ? node.sub.split(" · ").map((part) => <div key={part}>{part}</div>)
          : node.sub}
      </div>
      {node.tag && (
        <span className="mt-[7px] inline-block rounded-md border border-line bg-surface-2 px-[7px] py-0.5 font-jetbrains text-[10.5px] text-muted">
          {node.tag}
        </span>
      )}
    </div>
  );
}

export default function WorkflowCanvas({
  activeNodes,
  packetEdges,
  scenarioKey,
  animate,
}: WorkflowCanvasProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const portRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const packetRef = useRef<SVGCircleElement>(null);
  const overlayRef = useRef<SVGPathElement>(null);
  const litCountRef = useRef(0);
  const [ports, setPorts] = useState<Record<string, Point>>({});
  // Nodes light up progressively as the packet reaches them.
  const [litNodes, setLitNodes] = useState<Set<string>>(new Set());
  // Scale-to-fit (#183): the 1140px stage shrinks to the card's width on
  // narrow desktops instead of clipping/scrolling. Floor 0.6 = the worst
  // md-range container (702px at a 768px viewport needs 0.616), so every
  // desktop width fits scroll-free while the graph stays readable; below
  // the floor (mobile) the overflow-x-auto wrapper scrolls as before.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  // Below md the graph flows top-to-bottom (phones can't fit the columns).
  const [vertical, setVertical] = useState(false);
  useLayoutEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setVertical(!mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const stageW = vertical ? V_STAGE_W : STAGE_W;
  const stageH = vertical ? V_STAGE_H : STAGE_H;
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () =>
      setScale(Math.min(1, Math.max(0.6, el.clientWidth / stageW)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageW]);

  // Measure port centres (in stage coordinates) after layout + on resize/fonts.
  // Uses layout offsets, NOT getBoundingClientRect: rects include transforms,
  // and the scroll draw-in parks nodes at y:16 / scale:.96 — a re-measure
  // (fonts.ready, resize) mid-entrance baked that offset into every edge, so
  // lines ran below the ports. Offsets are transform-free, which also makes
  // the result invariant to the stage's scale-to-fit (#183).
  useLayoutEffect(() => {
    const measure = () => {
      const next: Record<string, Point> = {};
      for (const [key, el] of Object.entries(portRefs.current)) {
        const node = el?.offsetParent as HTMLElement | null;
        if (!el || !node) continue;
        // Port: absolute inside the node's padding box.
        next[key] = {
          x: node.offsetLeft + node.clientLeft + el.offsetLeft + el.offsetWidth / 2,
          y: node.offsetTop + node.clientTop + el.offsetTop + el.offsetHeight / 2,
        };
      }
      setPorts(next);
    };
    measure();
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [vertical]);

  // Edge geometry: smooth flow connector, tangents along the flow axis
  // (horizontal on desktop, vertical on phones).
  const edgeGeom = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of EDGES) {
      const a = ports[`${e.from}:out`];
      const b = ports[`${e.to}:in`];
      if (!a || !b) continue;
      if (vertical) {
        const k = Math.max(20, Math.abs(b.y - a.y) * 0.5);
        m[e.id] = `M${a.x} ${a.y} C${a.x} ${a.y + k} ${b.x} ${b.y - k} ${b.x} ${b.y}`;
        continue;
      }
      const k = Math.max(36, Math.abs(b.x - a.x) * 0.5);
      m[e.id] = `M${a.x} ${a.y} C${a.x + k} ${a.y} ${b.x - k} ${b.y} ${b.x} ${b.y}`;
    }
    return m;
  }, [ports, vertical]);

  // Combined path the packet follows: edge → across the shared node → edge…
  const packetD = useMemo(() => {
    if (!packetEdges.length || !edgeGeom[packetEdges[0]]) return "";
    let d = edgeGeom[packetEdges[0]];
    for (let i = 1; i < packetEdges.length; i++) {
      const cur = packetEdges[i];
      const sharedNode = cur.split(">")[0];
      const out = ports[`${sharedNode}:out`];
      const curve = edgeGeom[cur];
      if (!out || !curve) break;
      d += ` L ${out.x} ${out.y} ` + curve.replace(/^M[^A-Za-z]*/, "");
    }
    return d;
  }, [edgeGeom, packetEdges, ports]);

  // The nodes the packet visits, in order, with the time-fraction at which it
  // arrives at each (so they light up as it reaches them). Uses straight-line
  // segment lengths as a close approximation of arrival timing.
  const nodePlan = useMemo(() => {
    if (!packetEdges.length) return null;
    const order = [packetEdges[0].split(">")[0]];
    const cumulative: number[] = [];
    let total = 0;
    for (let i = 0; i < packetEdges.length; i++) {
      const [from, to] = packetEdges[i].split(">");
      const a = ports[`${from}:out`];
      const b = ports[`${to}:in`];
      if (!a || !b) return null;
      if (i > 0) {
        const inP = ports[`${from}:in`];
        const outP = ports[`${from}:out`];
        if (inP && outP) total += Math.hypot(outP.x - inP.x, outP.y - inP.y);
      }
      total += Math.hypot(b.x - a.x, b.y - a.y);
      order.push(to);
      cumulative.push(total);
    }
    return { order, arrivals: cumulative.map((c) => c / total) };
  }, [packetEdges, ports]);

  // Scroll-scrubbed draw-in: the graph sketches itself as the canvas scrolls
  // toward the viewport — edges draw stroke-by-stroke (dashoffset), each node
  // pops once its incoming edge reaches it. Scrubbed (not play-once) on
  // purpose: progress is a pure function of scroll position, so there is no
  // fire-once trigger that can die and no free-running tween to insure —
  // loading mid-page simply renders the finished drawing. clamp() keeps the
  // range inside the document on short pages. Recreated whenever the measured
  // port geometry changes (resize / fonts) so dash lengths stay true.
  useGSAP(
    () => {
      const stage = stageRef.current;
      if (!stage || !EDGES.every((e) => edgeGeom[e.id])) return;
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: stage,
            start: "clamp(top 96%)",
            end: "clamp(top 55%)",
            scrub: 0.4,
          },
        });
        const nodes = (ids: string[], at: number) =>
          tl.fromTo(
            ids.map((id) => stage.querySelector(`[data-draw-node="${id}"]`)),
            { autoAlpha: 0, y: 16, scale: 0.96 },
            { autoAlpha: 1, y: 0, scale: 1, duration: 0.35, stagger: 0.12, ease: "power2.out" },
            at,
          );
        const edges = (ids: string[], at: number) =>
          ids.forEach((id, i) => {
            const el = stage.querySelector<SVGPathElement>(`[data-draw-edge="${id}"]`);
            if (!el) return;
            const len = el.getTotalLength();
            gsap.set(el, { strokeDasharray: len, strokeDashoffset: len });
            tl.to(el, { strokeDashoffset: 0, duration: 0.55 }, at + i * 0.12);
          });
        // Left-to-right through the graph's columns.
        nodes(["trigger"], 0);
        edges(["trigger>switch"], 0.15);
        nodes(["switch"], 0.5);
        edges(["switch>ios", "switch>android", "switch>geo"], 0.65);
        nodes(["ios", "android", "geo"], 1.05);
        edges(["geo>de", "geo>web"], 1.3);
        nodes(["de", "web"], 1.7);
      });
    },
    { scope: stageRef, dependencies: [edgeGeom], revertOnUpdate: true },
  );

  // Draw the active path in magenta *as* the packet travels it (reveal via
  // stroke-dashoffset), and light each node when the packet reaches it.
  useLayoutEffect(() => {
    const packet = packetRef.current;
    const overlay = overlayRef.current;
    if (!overlay || !packet) return;

    if (!packetD) {
      overlay.style.opacity = "0";
      packet.style.opacity = "0";
      setLitNodes(new Set());
      litCountRef.current = 0;
      return;
    }

    const total = overlay.getTotalLength();
    overlay.style.opacity = "1";
    overlay.style.strokeDasharray = String(total);
    if (!total) return;

    // Reduced motion / off-screen: show the resolved path fully, no packet.
    if (!animate) {
      overlay.style.strokeDashoffset = "0";
      packet.style.opacity = "0";
      const all = nodePlan ? new Set(nodePlan.order) : new Set(activeNodes);
      setLitNodes(all);
      litCountRef.current = all.size;
      return;
    }

    // Start hidden, only the source node lit.
    overlay.style.strokeDashoffset = String(total);
    packet.style.opacity = "1";
    litCountRef.current = 1;
    setLitNodes(new Set(nodePlan ? [nodePlan.order[0]] : []));

    const TRAVEL = 1900;
    let raf = 0;
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now;
      const p = Math.min(1, (now - start) / TRAVEL);
      const drawn = p * total;
      overlay.style.strokeDashoffset = String(total - drawn);
      const pt = overlay.getPointAtLength(drawn);
      packet.setAttribute("cx", String(pt.x));
      packet.setAttribute("cy", String(pt.y));
      packet.style.opacity = p >= 1 ? "0" : "1";

      if (nodePlan) {
        let count = 1;
        for (const a of nodePlan.arrivals) if (p >= a) count++;
        if (count !== litCountRef.current) {
          litCountRef.current = count;
          setLitNodes(new Set(nodePlan.order.slice(0, count)));
        }
      }
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [scenarioKey, packetD, animate, activeNodes, nodePlan]);

  return (
    <div className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref-lg">
      <div className="flex h-[46px] items-center gap-2.5 border-b border-line bg-[linear-gradient(var(--color-surface),rgba(255,255,255,0.6))] px-4 font-jetbrains text-[12.5px] text-muted">
        {/* Phones drop the label and shorten the status so it stays one line. */}
        <span className="max-md:hidden">workflow:</span>
        <span className="whitespace-nowrap font-semibold text-brand-600">rii.link/summer-sale</span>
        <span className="ml-auto flex items-center gap-[7px] whitespace-nowrap font-semibold text-data-green">
          <span className="h-[7px] w-[7px] animate-status-pulse rounded-full bg-data-green" />
          <span><span className="max-md:hidden">tracing a </span>live visit</span>
        </span>
      </div>
      <div
        ref={scrollerRef}
        className="overflow-x-auto [background-image:radial-gradient(circle,rgba(16,16,40,0.07)_1.3px,transparent_1.3px)] [background-size:22px_22px]"
      >
        {/* Sizing shim: transforms don't affect layout, so this div reserves
            the SCALED footprint — the card shrinks with the stage and no
            dead space / needless scrollbar appears while the graph fits. */}
        <div
          className={vertical ? "mx-auto" : undefined}
          style={{ width: stageW * scale, height: stageH * scale }}
        >
          <div
            ref={stageRef}
            className="relative origin-top-left"
            style={{
              width: stageW,
              height: stageH,
              transform: scale === 1 ? undefined : `scale(${scale})`,
            }}
          >
          <svg
            className="pointer-events-none absolute inset-0 z-[1]"
            width={stageW}
            height={stageH}
            viewBox={`0 0 ${stageW} ${stageH}`}
          >
            {/* base connectors — always neutral; the active route is painted
                on top by the reveal overlay as the packet travels it. */}
            {EDGES.map((e) => {
              const d = edgeGeom[e.id];
              if (!d) return null;
              return (
                <path
                  key={e.id}
                  data-draw-edge={e.id}
                  d={d}
                  fill="none"
                  strokeLinecap="round"
                  stroke="var(--color-line-3)"
                  strokeWidth={2}
                />
              );
            })}
            {/* magenta reveal of the active route + the travelling packet.
                Dash offset / position are driven imperatively in the effect, so
                they are intentionally absent from JSX (re-renders won't reset
                them). The across-node segments are hidden behind the cards. */}
            {packetD && (
              <path
                ref={overlayRef}
                d={packetD}
                fill="none"
                strokeLinecap="round"
                stroke="var(--color-brand-600)"
                strokeWidth={2.5}
              />
            )}
            <circle
              ref={packetRef}
              r="4.5"
              fill="var(--color-brand-600)"
              style={{ filter: "drop-shadow(0 0 5px rgba(215,14,98,0.6))" }}
            />
          </svg>
          {NODES.map((node) => (
            <WorkflowNode
              key={node.id}
              node={node}
              active={litNodes.has(node.id)}
              vertical={vertical}
              registerPort={(key, el) => {
                portRefs.current[key] = el;
              }}
            />
          ))}
          </div>
        </div>
      </div>
    </div>
  );
}
